/**
 * iter87.199.58 — Landing "How it works" scroll-snap carousel.
 *
 * App Store-style tour of the app: 7 phone-framed mock screens on a
 * horizontal snap-scroll strip with captions and page dots.
 *
 * Behaviour contract (operator ratified 2026-02-16):
 *   - Auto-advance every 4s
 *   - Pause on hover (desktop) OR touch (mobile) — resumes 6s after
 *     the interaction ends so a reader can linger
 *   - Manual swipe / dot tap always wins and pauses auto-advance for
 *     the interaction window
 *   - `prefers-reduced-motion: reduce` → auto-advance disabled
 *   - Section can be tabbed into; arrow keys move slide-by-slide
 *
 * The carousel is the ONLY "how it works" explainer on the landing
 * page as of iter87.199.58 — the previous modal-only button was retired
 * per operator ask.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PhoneFrame from "./PhoneFrame";
import IntroVideo from "./IntroVideo";

// iter87.514 — Real app screenshots (replacing the drawn mockups).
// Images live at `frontend/public/screens/landing/`.
const SLIDES = [
    { key: "sign-up",      tone: "cyber", caption: "Start your crew.",                 img: "/screens/landing/01-start-your-crew.png",            alt: "Start your crew screen — crew name, username and email" },
    { key: "tournament",   tone: "cyber", caption: "Pick your tournament.",             img: "/screens/landing/02-pick-your-tournament.png",       alt: "Pick your tournament screen with NFL, CFB, EPL and La Liga options" },
    { key: "invite",       tone: "volt",  caption: "Bring the crew — invites in seconds.", img: "/screens/landing/03-bring-the-crew.png",         alt: "Bring the crew screen with pending email invites" },
    { key: "ready",        tone: "cyber", caption: "Review it, then launch.",            img: "/screens/landing/04-youre-all-set.png",             alt: "You're all set review screen before creating the tournament" },
    { key: "invite-email", tone: "volt",  caption: "Your crew gets the invite.",         img: "/screens/landing/06-invite-email.png",              alt: "Invite email with a one-click join link and sign-in codes" },
    { key: "join",         tone: "cyber", caption: "They pick a username, they're in.",  img: "/screens/landing/07-join-pick-username.png",        alt: "Join screen where an invited crewmate picks a username" },
    { key: "leaderboard",  tone: "volt",  caption: "Game on — watch the leaderboard.",   img: "/screens/landing/08-crew-dashboard-leaderboard.png", alt: "Crew dashboard with live leaderboard and weekly picks" },
];

const AUTO_ADVANCE_MS = 4000;
const RESUME_AFTER_INTERACTION_MS = 6000;

export default function HowItWorksCarousel({ onStartCta }) {
    const scrollerRef = React.useRef(null);
    const [active, setActive] = React.useState(0);
    const [paused, setPaused] = React.useState(false);
    const resumeTimerRef = React.useRef(null);

    // Respect a11y preference — this is the reader's call, not ours.
    const [reducedMotion] = React.useState(() =>
        typeof window !== "undefined" &&
        window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    );

    // Programmatic slide swap. `smooth` off during auto-advance to avoid
    // fighting with an in-flight user swipe; on for manual dot taps so
    // it feels intentional.
    const goTo = React.useCallback((idx, opts = {}) => {
        const n = SLIDES.length;
        const next = ((idx % n) + n) % n;
        const el = scrollerRef.current;
        if (!el) return;
        const child = el.children[next];
        if (child) {
            el.scrollTo({
                left: child.offsetLeft - (el.clientWidth - child.clientWidth) / 2,
                behavior: opts.smooth ? "smooth" : "auto",
            });
        }
        setActive(next);
    }, []);

    // Auto-advance loop. Gated on `paused` + reduced-motion.
    React.useEffect(() => {
        if (paused || reducedMotion) return;
        const t = setInterval(() => {
            setActive((cur) => {
                const next = (cur + 1) % SLIDES.length;
                const el = scrollerRef.current;
                const child = el?.children?.[next];
                if (el && child) {
                    el.scrollTo({
                        left: child.offsetLeft - (el.clientWidth - child.clientWidth) / 2,
                        behavior: "smooth",
                    });
                }
                return next;
            });
        }, AUTO_ADVANCE_MS);
        return () => clearInterval(t);
    }, [paused, reducedMotion]);

    // Track which slide is currently centred as the user swipes. Uses
    // IntersectionObserver so we don't spam scroll events on iOS.
    React.useEffect(() => {
        const el = scrollerRef.current;
        if (!el) return;
        const io = new IntersectionObserver(
            (entries) => {
                const visible = entries.filter((e) => e.isIntersecting)
                    .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
                if (visible) {
                    const idx = Number(visible.target.getAttribute("data-slide-idx"));
                    if (!Number.isNaN(idx)) setActive(idx);
                }
            },
            { root: el, threshold: [0.55, 0.75, 0.95] }
        );
        Array.from(el.children).forEach((c) => io.observe(c));
        return () => io.disconnect();
    }, []);

    // Pause helpers — used on hover / touch. `armResume` restarts the
    // auto-advance after the reader finishes interacting.
    const pauseAndArmResume = React.useCallback(() => {
        setPaused(true);
        if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
        resumeTimerRef.current = setTimeout(() => setPaused(false), RESUME_AFTER_INTERACTION_MS);
    }, []);

    React.useEffect(() => () => {
        if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    }, []);

    const onKeyDown = (e) => {
        if (e.key === "ArrowRight") { e.preventDefault(); goTo(active + 1, { smooth: true }); pauseAndArmResume(); }
        if (e.key === "ArrowLeft")  { e.preventDefault(); goTo(active - 1, { smooth: true }); pauseAndArmResume(); }
    };

    return (
        <section
            data-testid="how-it-works-section"
            aria-label="How it works"
            className="relative z-10 px-6 lg:px-12 pt-6 pb-14 lg:py-24 border-t border-white/5 bg-gradient-to-b from-transparent via-white/[0.015] to-transparent"
        >
            <div className="max-w-7xl mx-auto">
                {/* Section header — top margin tightened on mobile (iter87.199.59)
                    so the "HOW IT WORKS" pill peeks into the viewport as the
                    reader finishes the hero. Desktop keeps the generous rhythm. */}
                <div className="max-w-2xl mb-8 lg:mb-14">
                    <div className="inline-flex items-center gap-2 pill bg-ink text-cyber border-cyber mb-4">
                        <Sparkle /> How it works
                    </div>
                    <h2 className="font-display font-black tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl">
                        Set up in a minute.<br />
                        <span className="text-cyber">Play all season long.</span>
                    </h2>
                    <p className="text-base lg:text-lg text-zinc-400 mt-4 text-balance">
                        Swipe through the app before you sign up. Every screen is a real feature — no fluff, no ads, no bloat.
                    </p>
                </div>

                {/* iter87.468 — Intro video, above the phone carousel. A
                    real 30s voiceover walkthrough instead of just static
                    mock screens — autoplay stays off since it has sound. */}
                <div className="max-w-2xl mx-auto mb-10 lg:mb-16">
                    <IntroVideo testId="landing-intro-video" />
                </div>

                {/* Scroll strip */}
                <div
                    ref={scrollerRef}
                    data-testid="how-it-works-scroller"
                    tabIndex={0}
                    role="region"
                    aria-roledescription="carousel"
                    onKeyDown={onKeyDown}
                    onMouseEnter={pauseAndArmResume}
                    onTouchStart={pauseAndArmResume}
                    className="flex gap-6 overflow-x-auto snap-x snap-mandatory py-4 -mx-6 lg:-mx-12 px-6 lg:px-12
                               scrollbar-none [-ms-overflow-style:none] [scrollbar-width:none]
                               [&::-webkit-scrollbar]:hidden focus:outline-none"
                    style={{ scrollBehavior: "smooth", WebkitOverflowScrolling: "touch" }}
                >
                    {SLIDES.map((s, i) => {
                        return (
                            <div
                                key={s.key}
                                data-slide-idx={i}
                                data-testid={`how-it-works-slide-${s.key}`}
                                className="snap-center shrink-0 flex flex-col items-center gap-4"
                                aria-roledescription="slide"
                                aria-label={`${i + 1} of ${SLIDES.length}: ${s.caption}`}
                            >
                                <PhoneFrame tone={s.tone} label={s.key}>
                                    <img
                                        src={s.img}
                                        alt={s.alt}
                                        loading="lazy"
                                        className="w-full h-full object-cover object-top"
                                        data-testid={`how-it-works-screenshot-${s.key}`}
                                    />
                                </PhoneFrame>
                                <div
                                    data-testid={`how-it-works-caption-${s.key}`}
                                    className="max-w-[280px] text-center"
                                >
                                    <div className="text-[10px] font-mono font-black uppercase tracking-widest text-zinc-500 mb-1">
                                        {String(i + 1).padStart(2, "0")}
                                    </div>
                                    <div className="text-sm sm:text-base font-bold text-white leading-snug">
                                        {s.caption}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Page dots */}
                <div
                    data-testid="how-it-works-dots"
                    className="flex items-center justify-center gap-1.5 mt-6"
                    role="tablist"
                >
                    {SLIDES.map((s, i) => (
                        <button
                            key={s.key}
                            role="tab"
                            aria-selected={i === active}
                            aria-label={`Go to slide ${i + 1}`}
                            data-testid={`how-it-works-dot-${s.key}`}
                            onClick={() => { goTo(i, { smooth: true }); pauseAndArmResume(); }}
                            className={`h-1.5 rounded-full transition-all ${
                                i === active
                                    ? "w-6 bg-cyber"
                                    : "w-1.5 bg-white/20 hover:bg-white/40"
                            }`}
                        />
                    ))}
                </div>

                {/* Closer */}
                <div className="mt-14 lg:mt-20 flex flex-col items-center gap-5 text-center">
                    <p className="font-display font-black text-2xl sm:text-3xl lg:text-4xl tracking-tight text-white text-balance max-w-2xl">
                        Built to keep your <span className="text-cyber">tiny crew</span> tight.
                    </p>
                    <button
                        data-testid="how-it-works-cta"
                        onClick={onStartCta}
                        className="brutal-btn-primary text-sm sm:text-base py-3 px-8"
                    >
                        Start a rivalry for free →
                    </button>
                </div>

                {/* Pro+ teaser */}
                <div className="mt-14 lg:mt-20 flex flex-col items-center gap-3 text-center">
                    <p className="text-base sm:text-lg font-bold text-white">
                        Running more than one crew?
                    </p>
                    <p className="text-sm text-zinc-400 max-w-sm">
                        Unlock unlimited rivalries with premium features.
                    </p>
                    <Link
                        to="/rivalry-pro"
                        data-testid="how-it-works-pro-plus-cta"
                        className="brutal-btn bg-sky-400 text-black hover:bg-sky-300 border-transparent text-sm py-3 px-6 inline-flex items-center gap-2 mt-1"
                    >
                        Explore Rivalry Pro+ <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>
        </section>
    );
}

// Tiny inline sparkle so the section pill doesn't need to reach into
// lucide for a one-liner — keeps the bundle noise minimal.
function Sparkle() {
    return (
        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor" aria-hidden>
            <path d="M12 2l1.7 5.3H19l-4.3 3.1 1.7 5.3L12 12.6l-4.3 3.1 1.7-5.3L5 7.3h5.3L12 2z" />
        </svg>
    );
}
